import type { BudgetCeiling, BudgetReserve } from './api.ts';
import { applyPct, formatMoney, type Cents } from './money.ts';

/**
 * The client's budget split (feature spec "client page" §4; Jason, 2026-10-07, L-9): an amount, the project's
 * contingency, and where the contingency sits. Inside, the amount is all in and the contingency is held back from it;
 * on top, the amount is the work's and the contingency is added to it. Either way the contingency is the percentage
 * of the amount proposed. One rule for the API's reads, the form as it is typed and the confirmation's choice.
 */

export const BUDGET_RESERVES = ['inside', 'on_top'] as const satisfies readonly BudgetReserve[];

/** $400,000 at 10%: inside, $360,000 for the work and $40,000 held, $400,000 all in; on top, $400,000 for the work, $40,000 held, $440,000 all in. */
export function splitBudget(amountCents: Cents, contingencyPct: number, reserve: BudgetReserve): BudgetCeiling {
  const contingencyCents = applyPct(amountCents, contingencyPct);
  return reserve === 'on_top'
    ? { totalCents: amountCents + contingencyCents, workCents: amountCents, contingencyCents, contingencyPct, reserve }
    : { totalCents: amountCents, workCents: amountCents - contingencyCents, contingencyCents, contingencyPct, reserve };
}

/** The split in words: "$360,000 for the work, $40,000 held, $400,000 all in". */
export function splitWords(s: BudgetCeiling): string {
  return `${formatMoney(s.workCents)} for the work, ${formatMoney(s.contingencyCents)} held, ${formatMoney(s.totalCents)} all in`;
}

/** Where the contingency sits, after "with": "the contingency inside it", "the contingency on top". */
export function reserveWords(reserve: BudgetReserve): string {
  return reserve === 'on_top' ? 'the contingency on top' : 'the contingency inside it';
}
